import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, Mail, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIZES = [
  { id: 1, text: '5%', color: '#064E3B', textColor: '#fff', discount: 5 },
  { id: 2, text: 'Riprova', color: '#f5f5f5', textColor: '#333', discount: 0 },
  { id: 3, text: '10%', color: '#D4AF37', textColor: '#fff', discount: 10 },
  { id: 4, text: 'Riprova', color: '#f5f5f5', textColor: '#333', discount: 0 },
  { id: 5, text: '15%', color: '#064E3B', textColor: '#fff', discount: 15 },
  { id: 6, text: 'Spedizione\nGratis', color: '#D4AF37', textColor: '#fff', discount: 'shipping' },
  { id: 7, text: '5%', color: '#064E3B', textColor: '#fff', discount: 5 },
  { id: 8, text: 'Riprova', color: '#f5f5f5', textColor: '#333', discount: 0 },
];

const SpinWheel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSpinning, setIsSpinning] = useState(false);
  const [hasSpun, setHasSpun] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [prize, setPrize] = useState(null);
  const [showPrize, setShowPrize] = useState(false);
  const [step, setStep] = useState('email'); // 'email', 'spin', 'result'

  useEffect(() => {
    // Check if user already spun today
    const lastSpin = localStorage.getItem('qantri_last_spin');
    const today = new Date().toDateString();
    
    if (lastSpin === today) {
      setHasSpun(true);
      return;
    }

    // Show popup after 30 seconds for new visitors (less intrusive)
    // Don't show on important pages
    const importantPages = ['/checkout', '/login', '/register', '/vendor', '/admin', '/affiliate', '/reset-password'];
    const currentPath = window.location.pathname;
    if (importantPages.some(page => currentPath.includes(page))) {
      return;
    }

    const lastSeen = localStorage.getItem('qantri_wheel_last_seen');
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    // Only show if never seen or last seen more than 7 days ago
    if (!lastSeen || parseInt(lastSeen) < sevenDaysAgo) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        localStorage.setItem('qantri_wheel_last_seen', Date.now().toString());
      }, 30000); // 30 seconds instead of 5
      return () => clearTimeout(timer);
    }
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Inserisci un\'email valida');
      return;
    }

    try {
      // Save email to backend
      await axios.post(`${API}/wheel/register`, { email });
      setStep('spin');
    } catch (error) {
      // Continue anyway even if API fails
      setStep('spin');
    }
  };

  const spinWheel = () => {
    if (isSpinning || hasSpun) return;

    setIsSpinning(true);

    // Weighted random - more likely to get small prizes
    const weights = [25, 20, 15, 20, 5, 5, 25, 20]; // Higher = more likely
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    let random = Math.random() * totalWeight;
    
    let selectedIndex = 0;
    for (let i = 0; i < weights.length; i++) {
      random -= weights[i];
      if (random <= 0) {
        selectedIndex = i;
        break;
      }
    }

    const selectedPrize = PRIZES[selectedIndex];
    const segmentAngle = 360 / PRIZES.length;
    const targetRotation = 360 * 5 + (360 - (selectedIndex * segmentAngle + segmentAngle / 2));
    
    setRotation(targetRotation);
    setPrize(selectedPrize);

    setTimeout(() => {
      setIsSpinning(false);
      setShowPrize(true);
      setStep('result');
      setHasSpun(true);
      localStorage.setItem('qantri_last_spin', new Date().toDateString());

      // Save coupon if won
      if (selectedPrize.discount !== 0) {
        const couponCode = `QANTRI${selectedPrize.discount === 'shipping' ? 'SHIP' : selectedPrize.discount}${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
        localStorage.setItem('qantri_coupon', JSON.stringify({
          code: couponCode,
          discount: selectedPrize.discount,
          expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        }));
        
        // Also save to backend
        axios.post(`${API}/wheel/save-prize`, {
          email,
          prize: selectedPrize.discount,
          coupon_code: couponCode
        }).catch(() => {});
      }
    }, 5000);
  };

  const getPrizeMessage = () => {
    if (!prize) return '';
    if (prize.discount === 0) return 'Peccato! Torna domani per un altro giro!';
    if (prize.discount === 'shipping') return '🎉 Hai vinto la SPEDIZIONE GRATUITA!';
    return `🎉 Hai vinto uno sconto del ${prize.discount}%!`;
  };

  const getCouponCode = () => {
    const saved = localStorage.getItem('qantri_coupon');
    if (saved) {
      const { code } = JSON.parse(saved);
      return code;
    }
    return null;
  };

  if (hasSpun && !isOpen) return null;

  return (
    <>
      {/* Floating Button to reopen */}
      {!isOpen && !hasSpun && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.1 }}
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 left-4 z-40 w-14 h-14 bg-gradient-to-br from-[#D4AF37] to-[#B8960C] rounded-full shadow-lg flex items-center justify-center text-white"
          data-testid="wheel-floating-btn"
        >
          <Gift className="h-6 w-6" />
        </motion.button>
      )}

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => !isSpinning && setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl"
              data-testid="wheel-modal"
            >
              {/* Close button */}
              <button
                onClick={() => !isSpinning && setIsOpen(false)}
                className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600"
                disabled={isSpinning}
              >
                <X className="h-6 w-6" />
              </button>

              {/* Header */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 bg-[#064E3B]/10 text-[#064E3B] px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Sparkles className="h-4 w-4" />
                  Offerta Esclusiva
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Gira la Ruota!
                </h2>
                <p className="text-neutral-600 text-sm">
                  {step === 'email' && 'Inserisci la tua email per tentare la fortuna'}
                  {step === 'spin' && 'Clicca sulla ruota per girarla!'}
                  {step === 'result' && getPrizeMessage()}
                </p>
              </div>

              {/* Email Step */}
              {step === 'email' && (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                    <Input
                      type="email"
                      placeholder="La tua email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="wheel-email-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#064E3B] hover:bg-[#053d2f]"
                    data-testid="wheel-submit-email"
                  >
                    Tenta la Fortuna
                  </Button>
                  <p className="text-xs text-center text-neutral-500">
                    Riceverai offerte esclusive. Niente spam, promesso!
                  </p>
                </form>
              )}

              {/* Spin Step */}
              {(step === 'spin' || step === 'result') && (
                <div className="flex flex-col items-center">
                  {/* Wheel Container */}
                  <div className="relative w-64 h-64 md:w-72 md:h-72 mb-6">
                    {/* Pointer */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10">
                      <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-[#064E3B]" />
                    </div>
                    
                    {/* Wheel */}
                    <motion.div
                      className="w-full h-full rounded-full shadow-xl overflow-hidden"
                      style={{ 
                        rotate: rotation,
                        transition: isSpinning ? 'transform 5s cubic-bezier(0.2, 0.8, 0.3, 1)' : 'none'
                      }}
                      onClick={step === 'spin' && !isSpinning ? spinWheel : undefined}
                    >
                      <svg viewBox="0 0 100 100" className="w-full h-full cursor-pointer">
                        {PRIZES.map((item, index) => {
                          const angle = 360 / PRIZES.length;
                          const startAngle = index * angle - 90;
                          const endAngle = startAngle + angle;
                          const startRad = (startAngle * Math.PI) / 180;
                          const endRad = (endAngle * Math.PI) / 180;
                          
                          const x1 = 50 + 50 * Math.cos(startRad);
                          const y1 = 50 + 50 * Math.sin(startRad);
                          const x2 = 50 + 50 * Math.cos(endRad);
                          const y2 = 50 + 50 * Math.sin(endRad);
                          
                          const largeArc = angle > 180 ? 1 : 0;
                          
                          const textAngle = startAngle + angle / 2;
                          const textRad = (textAngle * Math.PI) / 180;
                          const textX = 50 + 32 * Math.cos(textRad);
                          const textY = 50 + 32 * Math.sin(textRad);

                          return (
                            <g key={item.id}>
                              <path
                                d={`M 50 50 L ${x1} ${y1} A 50 50 0 ${largeArc} 1 ${x2} ${y2} Z`}
                                fill={item.color}
                                stroke="#fff"
                                strokeWidth="0.5"
                              />
                              <text
                                x={textX}
                                y={textY}
                                fill={item.textColor}
                                fontSize="6"
                                fontWeight="bold"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                transform={`rotate(${textAngle + 90}, ${textX}, ${textY})`}
                              >
                                {item.text.split('\n').map((line, i) => (
                                  <tspan key={i} x={textX} dy={i === 0 ? 0 : 6}>{line}</tspan>
                                ))}
                              </text>
                            </g>
                          );
                        })}
                        {/* Center */}
                        <circle cx="50" cy="50" r="8" fill="#fff" stroke="#064E3B" strokeWidth="2" />
                        <text x="50" y="51" fill="#064E3B" fontSize="4" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                          GIRA
                        </text>
                      </svg>
                    </motion.div>
                  </div>

                  {/* Result */}
                  {step === 'result' && prize && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center"
                    >
                      {prize.discount !== 0 && (
                        <div className="bg-[#064E3B]/10 rounded-xl p-4 mb-4">
                          <p className="text-sm text-neutral-600 mb-2">Il tuo codice sconto:</p>
                          <p className="text-2xl font-bold text-[#064E3B] font-mono tracking-wider">
                            {getCouponCode()}
                          </p>
                          <p className="text-xs text-neutral-500 mt-2">Valido per 7 giorni</p>
                        </div>
                      )}
                      <Button 
                        onClick={() => setIsOpen(false)} 
                        className="w-full bg-[#064E3B] hover:bg-[#053d2f]"
                      >
                        {prize.discount !== 0 ? 'Usa Subito!' : 'Chiudi'}
                      </Button>
                    </motion.div>
                  )}

                  {step === 'spin' && !isSpinning && (
                    <p className="text-sm text-neutral-500 animate-pulse">
                      👆 Tocca la ruota per girare!
                    </p>
                  )}

                  {isSpinning && (
                    <p className="text-sm text-[#064E3B] font-medium">
                      🎰 La ruota sta girando...
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SpinWheel;
